import { useEffect, useState } from "react";
import api from "@/lib/api";

// The traveller's active groups, read once for the whole page: the floating
// group bubble and every "Share with my trip" button share the answer
// (owner, 2026-10-03: both only for a member of a group). Groups are kept a
// minute, "none" ten seconds, so a group made or joined a moment ago soon
// shows up; `refresh` (a new page) asks again once that time has passed.
const KEEP_SOME_MS = 60 * 1000;
const KEEP_NONE_MS = 10 * 1000;
const NONE = Object.freeze([]);
let cache = { userId: null, at: 0, groups: null, promise: null };

function isFresh(userId, now) {
  if (cache.userId !== userId || !cache.promise) return false;
  if (cache.groups === null) return true; // still on its way
  return now - cache.at < (cache.groups.length ? KEEP_SOME_MS : KEEP_NONE_MS);
}

export function activeGroups(userId, { fresh = false } = {}) {
  const now = Date.now();
  if (!fresh && isFresh(userId, now)) return cache.promise;
  const promise = api.get("/group-trips", { params: { offset: 0 } })
    .then(({ data }) => (Array.isArray(data?.items) ? data.items.filter((group) => group?.status === "active" && group.id) : NONE))
    .catch(() => NONE)
    .then((groups) => {
      if (cache.promise === promise) cache = { ...cache, at: Date.now(), groups };
      return groups;
    });
  cache = { userId, at: now, groups: null, promise };
  return promise;
}

/** Forget the answer (another account, a group just made, tests). */
export function forgetGroupMembership() {
  cache = { userId: null, at: 0, groups: null, promise: null };
}

/**
 * The signed-in traveller's active groups (each with its `unread` count);
 * none for a guest. `refresh` asks again when it changes; `pollMs` reads
 * afresh that often while the page is visible, and when it shows again.
 */
export function useActiveGroups(userId, refresh, { pollMs = 0 } = {}) {
  // The answer is kept with the account it is for: another account (or a
  // guest) never sees it, even for the moment before its own read returns.
  const [state, setState] = useState({ userId: null, groups: NONE });
  useEffect(() => {
    if (!userId) return undefined;
    let live = true;
    const take = (groups) => { if (live) setState({ userId, groups }); };
    activeGroups(userId).then(take);
    if (!pollMs) return () => { live = false; };
    const tick = () => { if (document.visibilityState !== "hidden") activeGroups(userId, { fresh: true }).then(take); };
    const timer = setInterval(tick, pollMs);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { live = false; clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [userId, refresh, pollMs]);
  return userId && state.userId === userId ? state.groups : NONE;
}

/** True once the signed-in traveller is known to belong to an active group; never for a guest. */
export default function useGroupMembership(userId) {
  return useActiveGroups(userId).length > 0;
}
